import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage, Sender } from '../types';
import { CosmosLogoIcon } from './icons/CosmosLogoIcon';
import { DocumentIcon } from './icons/DocumentIcon';
import { LandingChatSearchResults } from './LandingChatSearchResults';

interface LandingChatMessageProps {
  message: ChatMessage;
  onReadMore: () => void;
}

const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// Helper function to extract search results from attachments
const extractSearchResults = (attachments: any[]): any[] => {
    const searchResults: any[] = [];
    
    attachments.forEach((attachment: any) => {
        // Check if attachment contains search_results
        if (attachment.search_results && Array.isArray(attachment.search_results)) {
            searchResults.push(...attachment.search_results);
        }
        // Also check if the attachment itself is a search result object
        else if (attachment.score && attachment.metadata && attachment.source_type) {
            searchResults.push(attachment);
        }
    });
    
    return searchResults;
};

export const LandingChatMessage: React.FC<LandingChatMessageProps> = ({ 
  message,
  onReadMore
}) => {
  const isUser = message.sender === Sender.User;

  // Extract search results from attachments
  const searchResults = message.attachments ? extractSearchResults(message.attachments) : [];
  
  // Filter out search result attachments to get regular file attachments
  const regularAttachments = message.attachments ? message.attachments.filter((att: any) => 
    !att.search_results && !(att.score && att.metadata && att.source_type)
  ) : [];

  return (
    <div className={`flex items-start gap-2 sm:gap-4 p-2 sm:p-4 rounded-xl ${isUser ? 'bg-secondary-300/20' : ''} w-full min-w-0`}>
      {!isUser && (
        <div className="flex-shrink-0">
          <CosmosLogoIcon />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="markdown-content text-grayscale-1200 break-words overflow-wrap-anywhere">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.text}
          </ReactMarkdown>
        </div>
        
        {/* Render search results as mini cards for landing page */}
        {searchResults.length > 0 && (
          <div className="mt-2 sm:mt-3 w-full min-w-0">
            <LandingChatSearchResults 
              searchResults={searchResults}
              onReadMore={onReadMore}
            />
          </div>
        )}
        
        {/* Render regular file attachments */}
        {regularAttachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2 w-full">
            {regularAttachments.map((att, index) => (
              <div key={index} className="w-[100px] sm:w-[120px] h-[80px] sm:h-[94px] flex flex-col items-start rounded-lg border border-primary-200 shadow-custom-light relative bg-white flex-shrink-0">
                <div className="flex-1 self-stretch w-full rounded-t-lg bg-gray-200 flex items-center justify-center overflow-hidden">
                  {att.type && att.type.startsWith('image/') ? (
                    <img src={att.url} alt={att.name} className="w-full h-full object-cover"/>
                  ) : (
                    <DocumentIcon />
                  )}
                </div>
                <div className="p-1 self-stretch rounded-b-lg bg-primary-200">
                  <p className="self-stretch text-grayscale-1100 font-grotesk text-[10px] font-medium leading-none truncate">{att.name}</p>
                  <p className="self-stretch text-grayscale-1100 font-grotesk text-[10px] font-light leading-none">{formatBytes(att.size || 0)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
